import Card from "components/card";
import WithoutPageTable from "components/table/WithoutPageTable";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AddOrderItem from "../../draft-orders/components/AddOrderItem";
import Button from "components/button";
import SplitItemModal from "./SplitItemModal";
import RevertItemModal from "./RevertItemModal";
import { MdCheckCircle, MdOutlineError } from "react-icons/md";
import { CONTAINER_ITEM_MAPPING_STATUS } from "constants";
import { Link } from "react-router-dom";
import { ORDER_STATUS } from "constants";
import OrderConfirmModal from "../../order-page/components/OrderConfirmModal";
import { ORDER_TYPE } from "constants";
import ExportPdfButton from "./ExportPdfButton";
import DeleteItemModal from "./DeleteItemModal";
import { ImagePreview } from "components/modal/ImagePreview";

const columnHelper = createColumnHelper();
function ItemsTable({ refreshList }) {
  const { order: { afterDraft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const { orderDetailsData, orderDetailsLoading, orderDetailsError } =
    useSelector((state) => state.orders);

  const [selectedItem, setSelectedItem] = useState(null);
  const [openModal, setOpenModal] = useState(null);

  const orderItemsData = useMemo(
    () => orderDetailsData.items ?? [],
    [orderDetailsData]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("picture", {
        id: "image",
        header: "IMG",
        enableSorting: false,
        enableHiding: false,
        cell: ({ getValue }) => <ImagePreview url={getValue()} />,
      }),

      columnHelper.accessor("name", {
        id: "itemName",
        header: "ITEM NAME",
        enableSorting: true,
        enableHiding: false,
        cell: (info) => info.getValue() || "N/A",
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "DESCRIPTION",
        enableSorting: true,
        cell: (info) => info.getValue() || "N/A",
      }),
    ];

    // Extra columns for CARRYING orders
    const carryingColumns =
      orderDetailsData?.orderType === ORDER_TYPE.CARRYING
        ? [
            columnHelper.accessor("marka", {
              id: "marka",
              header: "MARKA",
              enableSorting: true,
              enableHiding: false,
              cell: (info) => info.getValue() || "N/A",
            }),
            columnHelper.accessor("ctn", {
              id: "ctn",
              header: "CTN",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("qty", {
              id: "qty",
              header: "QTY/CTN",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("kg", {
              id: "kg",
              header: "KG/CTN",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("cbm", {
              id: "cbm",
              header: "CBM/CTN",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("rate", {
              id: "rate",
              header: "RATE",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("totalAmount", {
              id: "totalAmount",
              header: "TOTAL AMOUNT",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("totalQty", {
              id: "totalQty",
              header: "TOTAL QTY",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("totalKg", {
              id: "totalKg",
              header: "TOTAL KG",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("totalCBM", {
              id: "totalCBM",
              header: "TOTAL CBM",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
          ]
        : [
            // For other orders, you may want to keep totalQty only
            columnHelper.accessor("totalQty", {
              id: "totalQty",
              header: "QTY",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
            columnHelper.accessor("price", {
              id: "price",
              header: "PRICE",
              enableSorting: true,
              cell: (info) => info.getValue() ?? 0,
            }),
          ];

    // Columns common to all types (Container, Status, Actions)
    const commonEndColumns = [
      columnHelper.accessor((row) => row?.container?.containerNo || "", {
        id: "container",
        header: "CONTAINER",
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          const containerId = row?.container?.containerId;
          const containerNo = info.getValue();
          return containerId ? (
            <Link
              to={`/containers/${containerId}`}
              className="cursor-pointer font-medium text-blue-600 hover:underline"
            >
              {containerNo || "N/A"}
            </Link>
          ) : (
            "N/A"
          );
        },
      }),
      columnHelper.accessor(
        (row) => row.containerMappingStatus?.enumVal || "",
        {
          id: "containerMappingStatus",
          header: "STATUS",
          cell: (info) => {
            const status = info.row.original.containerMappingStatus;
            if (!status) return "-";
            const statusKey = status.enumVal;
            const statusName = status.name || "-";

            return (
              <div className="flex items-center">
                {statusKey === CONTAINER_ITEM_MAPPING_STATUS.CLEARED ? (
                  <MdCheckCircle className="me-1 text-green-500 dark:text-green-300" />
                ) : statusKey === CONTAINER_ITEM_MAPPING_STATUS.SUSPENSE ? (
                  <MdOutlineError className="me-1 text-amber-500 dark:text-amber-300" />
                ) : null}
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  {statusName}
                </p>
              </div>
            );
          },
        }
      ),
      columnHelper.display({
        id: "actions",
        header: "ACTIONS",
        cell: ({ row }) => {
          const { containerMappingStatus, parentId, isParentAssign } =
            row.original;
          if (
            containerMappingStatus?.enumVal !==
            CONTAINER_ITEM_MAPPING_STATUS.CLEARED
          ) {
            return (
              <div className="flex gap-2">
                {parentId === null && afterDraft?.split && (
                  <Button
                    className="py-1 text-sm"
                    onClick={() => {
                      setSelectedItem(row.original);
                      setOpenModal("split");
                    }}
                  >
                    Split
                  </Button>
                )}
                {parentId !== null && !isParentAssign && afterDraft?.revert && (
                  <Button
                    variant="orange"
                    onClick={() => {
                      setSelectedItem(row.original);
                      setOpenModal("revert");
                    }}
                  >
                    Revert
                  </Button>
                )}
                {afterDraft?.itemDelete && (
                  <Button
                    variant="red"
                    onClick={() => {
                      setSelectedItem(row.original);
                      setOpenModal("delete");
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            );
          }
          return "N/A";
        },
      }),
    ];

    // Combine all columns
    return [...baseColumns, ...carryingColumns, ...commonEndColumns];
  }, [orderDetailsData?.orderType]);

  const initialColumnVisibility = {
    image: true,
    itemName: true,
    description: false,
    container: true,
    containerMappingStatus: true,
    actions: true,
    totalQty: true,
    price: true,
  };
  const initialColumnVisibilityCarrying = {
    image: true,
    itemName: true,
    description: false,
    container: true,
    containerMappingStatus: true,
    actions: true,

    marka: true,
    ctn: true,
    qty: true,
    kg: false,
    cbm: false,
    rate: false,
    totalAmount: false,
    totalQty: true,
    totalKg: false,
    totalCBM: false,
  };

  return (
    <>
      {openModal === "split" && (
        <SplitItemModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}

      {openModal === "revert" && (
        <RevertItemModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}
      {openModal === "delete" && (
        <DeleteItemModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}
      <Card extra={`w-full h-full sm:overflow-auto px-6`}>
        <div className="mb-5 w-full  flex-col items-center lg:pl-0">
          <WithoutPageTable
            tableTitle={`Order : ${orderDetailsData?.orderNo ?? "N/A"}
            `}
            actionButton={
              <>
                <ExportPdfButton />
                {orderDetailsData?.status?.enumVal !== ORDER_STATUS.CANCELLED &&
                  afterDraft?.statusChange && (
                    <OrderConfirmModal
                      data={orderDetailsData}
                      refreshList={refreshList}
                      isComfirm={false}
                    />
                  )}
                {afterDraft?.itemAdd && (
                  <AddOrderItem
                    orderId={orderDetailsData?._id}
                    refreshList={refreshList}
                    orderType={orderDetailsData?.orderType}
                  />
                )}
              </>
            }
            columns={columns}
            data={orderItemsData ?? []}
            loading={orderDetailsLoading}
            error={orderDetailsError}
            initialColumnVisibility={
              orderDetailsData?.orderType === ORDER_TYPE.CARRYING
                ? initialColumnVisibilityCarrying
                : initialColumnVisibility
            }
            isColumnDropdown={true}
          />
        </div>
      </Card>
    </>
  );
}

export default ItemsTable;
